
import { Gamepad2, Plus, AlertCircle, Archive, CheckCircle, Code, Zap, Target, Trophy, Swords, Puzzle, Sparkles, XCircle, ArrowUpDown, ChevronDown } from 'lucide-react'
import { useEffect, useState, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useProjectsStore } from '../store/useProjectsStore'
import { useTeamsStore } from '../store/useTeamsStore'
import { useCategoriesStore } from '../store/useCategoriesStore'
import { useToastStore } from '../store/useToastStore'
import { projectsService } from '../services/projectsService'
import SmoothLoadingSpinner from '../components/common/SmoothLoadingSpinner'
import CreateGameModal from '../components/common/CreateGameModal'
import { ProjectEditModal } from '../components/projects/ProjectEditModal'
import ConfirmationModal from '../components/common/ConfirmationModal'
import { ProjectTransferModal } from '../components/projects/ProjectTransferModal'
import { CompletedGameCard } from '../components/projects/CompletedGameCard'
import { DevelopmentGameCard } from '../components/projects/DevelopmentGameCard'
import { ArchivedGameCard } from '../components/projects/ArchivedGameCard'
import type { Game } from '../types/api'
import { FILL_GRADIENT, LINE_GRADIENT } from '../constants/gradients'
import { analyticsService } from '../services/analyticsService'

// Helper function to format time
const formatTimeAgo = (dateString: string) => {
  if (!dateString) {
    return 'Unknown';
  }
  
  const date = new Date(dateString)
  
  if (isNaN(date.getTime())) {
    return 'Invalid';
  }
  
  const now = new Date()
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)
  
  if (diffInSeconds < 60) return 'just now'
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`
  const days = Math.floor(diffInSeconds / 86400)
  if (diffInSeconds < 2592000) return `${days} day${days === 1 ? '' : 's'} ago`
  return date.toLocaleDateString()
}

// Helper function to get tag styling
const getTagStyle = (tag: string, index: number) => {
  const tagLower = tag.toLowerCase()
  
  // Define tag styles with icons
  const tagStyles: Record<string, { icon: any, bgColor: string, textColor: string, label: string }> = {
    'action': { icon: Zap, bgColor: 'bg-orange-100 dark:bg-orange-900/30', textColor: 'text-orange-700 dark:text-orange-400', label: 'Action' },
    'puzzle': { icon: Puzzle, bgColor: 'bg-purple-100 dark:bg-purple-900/30', textColor: 'text-purple-700 dark:text-purple-400', label: 'Puzzle' },
    'strategy': { icon: Target, bgColor: 'bg-blue-100 dark:bg-blue-900/30', textColor: 'text-blue-700 dark:text-blue-400', label: 'Strategy' },
    'arcade': { icon: Trophy, bgColor: 'bg-yellow-100 dark:bg-yellow-900/30', textColor: 'text-yellow-700 dark:text-yellow-400', label: 'Arcade' },
    'adventure': { icon: Swords, bgColor: 'bg-red-100 dark:bg-red-900/30', textColor: 'text-red-700 dark:text-red-400', label: 'Adventure' },
    'casual': { icon: Sparkles, bgColor: 'bg-pink-100 dark:bg-pink-900/30', textColor: 'text-pink-700 dark:text-pink-400', label: 'Casual' },
  }
  
  // Find matching style or use default
  const matchedKey = Object.keys(tagStyles).find(key => tagLower.includes(key))
  if (matchedKey) {
    return tagStyles[matchedKey]
  }
  
  // Default colors cycle
  const defaultStyles = [
    { icon: Gamepad2, bgColor: 'bg-gray-100 dark:bg-gray-800', textColor: 'text-gray-700 dark:text-gray-400', label: tag },
    { icon: Sparkles, bgColor: 'bg-teal-100 dark:bg-teal-900/30', textColor: 'text-teal-700 dark:text-teal-400', label: tag },
    { icon: Target, bgColor: 'bg-indigo-100 dark:bg-indigo-900/30', textColor: 'text-indigo-700 dark:text-indigo-400', label: tag },
  ]
  
  return { ...defaultStyles[index % defaultStyles.length], label: tag.charAt(0).toUpperCase() + tag.slice(1) }
}

const ProjectsPage = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [showCreateGameModal, setShowCreateGameModal] = useState(false)
  
  // Get initial tab from URL or default to 'completed'
  const tabFromUrl = searchParams.get('tab') as 'completed' | 'in-development' | 'archived' | null
  const validTabs = ['completed', 'in-development', 'archived']
  const initialTab = tabFromUrl && validTabs.includes(tabFromUrl) ? tabFromUrl : 'completed'
  
  const [activeTab, setActiveTab] = useState<'completed' | 'in-development' | 'archived'>(initialTab)
  const [showSortDropdown, setShowSortDropdown] = useState(false)
  const [sortBy, setSortBy] = useState<'recent' | 'popular' | 'name' | 'plays'>('recent')
  const [publishedCollapsed, setPublishedCollapsed] = useState(false)
  const [notPublishedCollapsed, setNotPublishedCollapsed] = useState(false)
  
  // Modal states for dropdown actions
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isArchiveConfirmOpen, setIsArchiveConfirmOpen] = useState(false)
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false)
  const [selectedProjectForAction, setSelectedProjectForAction] = useState<Game | null>(null)
  const [isActionInProgress, setIsActionInProgress] = useState(false)
  
  const {
    projects,
    isLoadingProjects,
    projectsError,
    fetchProjects,
    refreshProjects,
    searchProjects,
    isInitialized,
    duplicateProject,
    deleteProject,
    updateProject,
    togglePublishStatus,
    toggleVisibility,
  } = useProjectsStore()

  const { refreshTeams } = useTeamsStore()
  const { fetchCategories } = useCategoriesStore()
  const { showToast } = useToastStore()

  const [searchQuery, setSearchQuery] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const hasInitiatedFetch = useRef(false)
  const searchTimeoutRef = useRef<NodeJS.Timeout>()
  const previousSearchQuery = useRef('')

  // Helper function to change tab and update URL
  const changeTab = (newTab: 'completed' | 'in-development' | 'archived') => {
    setActiveTab(newTab)
    setSearchParams({ tab: newTab })
    
    // Track tab change
    analyticsService.track({
      name: 'projects_tab_changed',
      properties: {
        tab: newTab,
        from_tab: activeTab
      }
    });
  }

  // Sync activeTab with URL changes (for browser back/forward)
  useEffect(() => {
    const tabParam = searchParams.get('tab') as 'completed' | 'in-development' | 'archived' | null
    if (tabParam && validTabs.includes(tabParam) && tabParam !== activeTab) {
      setActiveTab(tabParam)
    }
  }, [searchParams])

  // Effect for initial load only
  useEffect(() => {
    if (!hasInitiatedFetch.current && !isInitialized && !isLoadingProjects) {
      console.log('📂 ProjectsPage: Initial load - fetching projects and categories...')
      hasInitiatedFetch.current = true
      fetchProjects()
      fetchCategories() // ✅ Load categories for proper card rendering
    }
  }, []) // Only run once on mount

  // Separate effect for search with debouncing
  useEffect(() => {
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    // Only process search changes, not initial empty state
    if (searchQuery === previousSearchQuery.current) {
      return
    }

    searchTimeoutRef.current = setTimeout(async () => {
      if (searchQuery.trim()) {
        console.log('🔍 Searching for:', searchQuery.trim())
        setIsSearching(true)
        try {
          await searchProjects(searchQuery.trim())
        } finally {
          setIsSearching(false)
        }
      } else if (searchQuery === '' && previousSearchQuery.current !== '') {
        // Only restore original list when actually clearing a search
        console.log('🔄 Clearing search, restoring original list')
        await fetchProjects()
      }
      
      previousSearchQuery.current = searchQuery
    }, 300)

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [searchQuery, searchProjects, fetchProjects])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (showSortDropdown) {
        setShowSortDropdown(false)
      }
    }

    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [showSortDropdown])
  
  // Track sort changes
  useEffect(() => {
    if (sortBy) {
      analyticsService.track({
        name: 'projects_sorted',
        properties: {
          sort_by: sortBy,
          active_tab: activeTab
        }
      });
    }
  }, [sortBy])
  
  // Handle project transfer
  const handleProjectTransfer = async (targetTeamId: string) => {
    if (!selectedProjectForAction) return
    
    try {
      console.log('🔄 Starting project transfer to team:', targetTeamId)
      
      // Call the transfer API
      await projectsService.transferProject(selectedProjectForAction.id, targetTeamId)
      
      // Track successful transfer
      analyticsService.track({
        name: 'project_transferred',
        properties: {
          project_id: selectedProjectForAction.id,
          project_name: selectedProjectForAction.name,
          from_team_id: selectedProjectForAction.team_id,
          to_team_id: targetTeamId
        }
      });
      
      // Refresh projects and teams to reflect the change
      await Promise.all([refreshProjects(), refreshTeams()])
      
      console.log('✅ Project transferred successfully!')
      showToast('success', `"${selectedProjectForAction.name}" transferred successfully!`)
      
    } catch (error) {
      console.error('❌ Failed to transfer project:', error)
      showToast('error', 'Failed to transfer project. Please try again.')
      throw error // Re-throw so the modal can handle the error
    }
  }

  // Handle project duplication
  const handleDuplicate = async (project: Game) => {
    setIsActionInProgress(true)
    try {
      console.log('🔄 Duplicating project:', project.name)
      const duplicatedProject = await duplicateProject(project.id, `${project.name} (Copy)`)
      console.log('✅ Project duplicated successfully!')
      
      // Track duplication
      analyticsService.track({
        name: 'project_duplicated',
        properties: {
          source_project_id: project.id,
          source_project_name: project.name,
          target_project_id: duplicatedProject.id,
          target_name: `${project.name} (Copy)`,
          has_version: !!project.current_version_id
        }
      });
      
      // Navigate to appropriate page based on project state
      const targetPath = project.current_version_id 
        ? `/projects/${duplicatedProject.id}` 
        : `/editor/${duplicatedProject.id}`
      navigate(targetPath)
    } catch (error) {
      console.error('❌ Failed to duplicate project:', error)
      showToast('error', 'Failed to duplicate project. Please try again.')
    } finally {
      setIsActionInProgress(false)
    }
  }

  // Handle publish/unpublish toggle
  const handleTogglePublish = async (project: Game) => {
    setIsActionInProgress(true)
    try {
      const isPublished = project.status === 'active'
      const action = isPublished ? 'Unpublishing' : 'Publishing'
      const actionPast = isPublished ? 'unpublished' : 'published'
      
      console.log(`🔄 ${action} project:`, project.name)
      await togglePublishStatus(project.id, isPublished)
      console.log(`✅ Project ${actionPast} successfully!`)
      
      // Track publish/unpublish
      analyticsService.track({
        name: isPublished ? 'project_unpublished' : 'project_published',
        properties: {
          project_id: project.id,
          project_name: project.name,
          version: project.current_bundle_version
        }
      });
      
      showToast('success', `"${project.name}" ${actionPast} successfully!`)
    } catch (error) {
      console.error(`❌ Failed to toggle publish status:`, error)
      showToast('error', `Failed to ${project.status === 'active' ? 'unpublish' : 'publish'} project. Please try again.`)
    } finally {
      setIsActionInProgress(false)
    }
  }

  // Handle visibility toggle
  const handleToggleVisibility = async (project: Game) => {
    setIsActionInProgress(true)
    try {
      const isPrivate = project.content?.visibility === 'private'
      const action = isPrivate ? 'Making public' : 'Making private'
      const actionPast = isPrivate ? 'made public' : 'made private'
      
      console.log(`🔒 ${action} project:`, project.name)
      await toggleVisibility(project.id, !isPrivate)
      console.log(`✅ Project ${actionPast} successfully!`)
      showToast('success', `"${project.name}" ${actionPast} successfully!`)
    } catch (error) {
      console.error(`❌ Failed to toggle visibility:`, error)
      showToast('error', `Failed to change visibility. Please try again.`)
    } finally {
      setIsActionInProgress(false)
    }
  }

  return (
    <div className="h-full flex flex-col p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-dark-text">Your Games</h1>
          <p className="text-gray-600 dark:text-dark-text-muted mt-1">
            Manage your livestream games across TikTok, Twitch, and YouTube
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button 
            onClick={() => setShowCreateGameModal(true)}
            className="px-4 py-2 text-sm font-semibold text-white rounded-lg hover:shadow-lg hover:scale-105 active:scale-95 transition-all duration-200 flex items-center gap-2"
            style={{ background: FILL_GRADIENT }}
          >
            <Plus className="w-4 h-4" />
            Create Game
          </button>
        </div>
      </div>

      {/* Error Message */}
      {projectsError && (
        <div className="card p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 mb-6">
          <div className="flex items-center space-x-2">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
            <div>
              <p className="text-sm font-medium text-red-800 dark:text-red-200">
                Error loading projects
              </p>
              <p className="text-sm text-red-600 dark:text-red-300 mt-1">{projectsError}</p>
            </div>
          </div>
        </div>
      )}

      {/* Status Tabs */}
      <div className="flex items-center justify-between gap-4 border-b border-gray-200 dark:border-dark-border">
        <div className="flex">
          <button
            onClick={() => changeTab('completed')}
            className={`relative flex items-center gap-2 px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === 'completed'
                ? 'text-gray-900 dark:text-dark-text'
                : 'text-gray-500 dark:text-dark-text-muted hover:text-gray-900 dark:hover:text-dark-text'
            }`}
          >
            <CheckCircle className="w-4 h-4" />
            Completed
            {activeTab === 'completed' && (
              <div 
                className="absolute -bottom-1 left-0 right-0 h-1"
                style={{ background: LINE_GRADIENT }}
              />
            )}
          </button>
          <button
            onClick={() => changeTab('in-development')}
            className={`relative flex items-center gap-2 px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === 'in-development'
                ? 'text-gray-900 dark:text-dark-text'
                : 'text-gray-500 dark:text-dark-text-muted hover:text-gray-900 dark:hover:text-dark-text'
            }`}
          >
            <Code className="w-4 h-4" />
            In Development
            {activeTab === 'in-development' && (
              <div 
                className="absolute -bottom-1 left-0 right-0 h-1"
                style={{ background: LINE_GRADIENT }}
              />
            )}
          </button>
          <button
            onClick={() => changeTab('archived')}
            className={`relative flex items-center gap-2 px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === 'archived'
                ? 'text-gray-900 dark:text-dark-text'
                : 'text-gray-500 dark:text-dark-text-muted hover:text-gray-900 dark:hover:text-dark-text'
            }`}
          >
            <Archive className="w-4 h-4" />
            Archived
            {activeTab === 'archived' && (
              <div 
                className="absolute -bottom-1 left-0 right-0 h-1"
                style={{ background: LINE_GRADIENT }}
              />
            )}
          </button>
        </div>

        {/* Sort By Dropdown */}
        <div className="relative pb-3" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => setShowSortDropdown(!showSortDropdown)}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-white dark:bg-dark-surface border border-gray-300 dark:border-dark-border text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors flex items-center gap-2"
          >
            <ArrowUpDown className="w-4 h-4" />
            <span>Sort by: {sortBy === 'recent' ? 'Recent' : sortBy === 'popular' ? 'Popular' : sortBy === 'name' ? 'Name' : 'Plays'}</span>
          </button>

          {/* Sort Dropdown Menu */}
          {showSortDropdown && (
            <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-10">
              <button
                onClick={() => {
                  setSortBy('recent')
                  setShowSortDropdown(false)
                }}
                className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 ${
                  sortBy === 'recent' ? 'text-pink-600 dark:text-pink-400 font-medium' : 'text-gray-700 dark:text-gray-300'
                }`}
              >
                {sortBy === 'recent' && <CheckCircle className="w-4 h-4" />}
                <span className={sortBy === 'recent' ? '' : 'ml-6'}>Recent</span>
              </button>
              <button
                onClick={() => {
                  setSortBy('popular')
                  setShowSortDropdown(false)
                }}
                className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 ${
                  sortBy === 'popular' ? 'text-pink-600 dark:text-pink-400 font-medium' : 'text-gray-700 dark:text-gray-300'
                }`}
              >
                {sortBy === 'popular' && <CheckCircle className="w-4 h-4" />}
                <span className={sortBy === 'popular' ? '' : 'ml-6'}>Popular</span>
              </button>
              <button
                onClick={() => {
                  setSortBy('name')
                  setShowSortDropdown(false)
                }}
                className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 ${
                  sortBy === 'name' ? 'text-pink-600 dark:text-pink-400 font-medium' : 'text-gray-700 dark:text-gray-300'
                }`}
              >
                {sortBy === 'name' && <CheckCircle className="w-4 h-4" />}
                <span className={sortBy === 'name' ? '' : 'ml-6'}>Name</span>
              </button>
              <button
                onClick={() => {
                  setSortBy('plays')
                  setShowSortDropdown(false)
                }}
                className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 ${
                  sortBy === 'plays' ? 'text-pink-600 dark:text-pink-400 font-medium' : 'text-gray-700 dark:text-gray-300'
                }`}
              >
                {sortBy === 'plays' && <CheckCircle className="w-4 h-4" />}
                <span className={sortBy === 'plays' ? '' : 'ml-6'}>Plays</span>
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto pr-4">
        {/* Projects Content */}
        {(isLoadingProjects || isSearching) ? (
          <SmoothLoadingSpinner 
            isLoading={isLoadingProjects || isSearching} 
            size="lg" 
            message={isSearching ? "Searching..." : "Loading projects..."} 
            className="py-12" 
            delay={100}
          />
        ) : (() => {
          // Filter projects based on active tab
          let filteredProjects = projects.filter((p: Game) => {
            if (activeTab === 'completed') {
              // Completed: has a version (current_version_id exists) and not archived
              return p.current_version_id != null && p.archived_at == null;
            } else if (activeTab === 'in-development') {
              // In Development: no version yet and not archived
              return p.current_version_id == null && p.archived_at == null;
            } else if (activeTab === 'archived') {
              // Archived: archived_at is not null
              return p.archived_at != null;
            }
            return true;
          });

          // Sort filtered projects
          filteredProjects = [...filteredProjects].sort((a, b) => {
            switch (sortBy) {
              case 'name':
                return (a.name || '').localeCompare(b.name || '');
              case 'popular':
                return (b.total_downloads || 0) - (a.total_downloads || 0);
              case 'plays':
                // For plays, we don't have real data, so sort by downloads as proxy
                return (b.total_downloads || 0) - (a.total_downloads || 0);
              case 'recent':
              default:
                return new Date(b.updated_at || b.created_at).getTime() - new Date(a.updated_at || a.created_at).getTime();
            }
          });

          return filteredProjects.length === 0 ? (
          <div className="text-center py-12">
            <Gamepad2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-dark-text mb-2">
                {searchQuery ? 'No projects found' : `No ${activeTab === 'completed' ? 'completed' : activeTab === 'in-development' ? 'in development' : 'archived'} projects`}
            </h3>
            <p className="text-gray-500 dark:text-dark-text-muted">
              {searchQuery 
                ? `No projects match "${searchQuery}". Try a different search term.`
                  : activeTab === 'completed' 
                    ? 'Projects with at least one version will appear here.'
                    : activeTab === 'in-development'
                      ? 'Projects without versions will appear here.'
                      : 'Archived projects will appear here.'
              }
            </p>
              {!searchQuery && activeTab === 'in-development' && (
              <button 
                onClick={() => setShowCreateGameModal(true)}
                className="btn btn-primary mt-4"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Project
              </button>
            )}
          </div>
        ) : activeTab === 'completed' ? (
          <>
            {(() => {
              const publishedProjects = filteredProjects.filter((p: Game) => p.status === 'active')
              const notPublishedProjects = filteredProjects.filter((p: Game) => p.status === 'inactive' && p.current_version_id)
              
              return (
                <div className="space-y-8 pt-6">
                  {/* Published Section */}
                  {publishedProjects.length > 0 && (
                    <div>
                      <h2
                        className="sticky top-0 bottom-0 z-10 text-xl font-medium text-green-700 dark:text-green-400 mb-4 flex items-center gap-2 px-4 py-2.5 bg-gray-50 dark:bg-dark-bg cursor-pointer select-none"
                        onClick={() => setPublishedCollapsed(c => !c)}
                      >
                        <CheckCircle className="w-5 h-5" />
                        Published
                        <ChevronDown className={`w-4 h-4 ml-auto transition-transform duration-200 ${publishedCollapsed ? '-rotate-90' : ''}`} />
                      </h2>
                      {!publishedCollapsed && <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {publishedProjects.map((project: Game, index: number) => (
                          <CompletedGameCard
                            key={`published-${project.id}-${index}`}
                            project={project}
                            index={index}
                            onArchive={(project) => {
                              setSelectedProjectForAction(project)
                              setIsArchiveConfirmOpen(true)
                            }}
                            onEdit={(project) => {
                              setSelectedProjectForAction(project)
                              setIsEditModalOpen(true)
                            }}
                            onDuplicate={handleDuplicate}
                            onTogglePublish={handleTogglePublish}
                            onToggleVisibility={handleToggleVisibility}
                            onTransfer={(project) => {
                              setSelectedProjectForAction(project)
                              setIsTransferModalOpen(true)
                            }}
                            isActionInProgress={isActionInProgress}
                            formatTimeAgo={formatTimeAgo}
                            getTagStyle={getTagStyle}
                          />
                        ))}
                      </div>}
                    </div>
                  )}
                  
                  {/* Not Published Section */}
                  {notPublishedProjects.length > 0 && (
                    <div>
                      <h2
                        className="sticky top-0 bottom-0 z-10 text-xl font-medium text-red-700 dark:text-red-400 mb-4 flex items-center gap-2 px-4 py-2.5 bg-gray-50 dark:bg-dark-bg cursor-pointer select-none"
                        onClick={() => setNotPublishedCollapsed(c => !c)}
                      >
                        <XCircle className="w-5 h-5" />
                        Not Published
                        <ChevronDown className={`w-4 h-4 ml-auto transition-transform duration-200 ${notPublishedCollapsed ? '-rotate-90' : ''}`} />
                      </h2>
                      {!notPublishedCollapsed && <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {notPublishedProjects.map((project: Game, index: number) => (
                          <CompletedGameCard
                            key={`not-published-${project.id}-${index}`}
                            project={project}
                            index={index}
                            onArchive={(project) => {
                              setSelectedProjectForAction(project)
                              setIsArchiveConfirmOpen(true)
                            }}
                            onEdit={(project) => {
                              setSelectedProjectForAction(project)
                              setIsEditModalOpen(true)
                            }}
                            onDuplicate={handleDuplicate}
                            onTogglePublish={handleTogglePublish}
                            onToggleVisibility={handleToggleVisibility}
                            onTransfer={(project) => {
                              setSelectedProjectForAction(project)
                              setIsTransferModalOpen(true)
                            }}
                            isActionInProgress={isActionInProgress}
                            formatTimeAgo={formatTimeAgo}
                            getTagStyle={getTagStyle}
                          />
                        ))}
                      </div>}
                    </div>
                  )}
                </div>
              )
            })()}
          </>
        ) : activeTab === 'in-development' ? (
          <div className="space-y-3 pt-6">
            {filteredProjects.map((project: Game, index) => (
              <DevelopmentGameCard
                key={`dev-${project.id}-${index}`}
                project={project}
                index={index}
                onArchive={(project) => {
                  setSelectedProjectForAction(project)
                  setIsArchiveConfirmOpen(true)
                }}
                onEdit={(project) => {
                  setSelectedProjectForAction(project)
                  setIsEditModalOpen(true)
                }}
                onDuplicate={handleDuplicate}
                onTransfer={(project) => {
                  setSelectedProjectForAction(project)
                  setIsTransferModalOpen(true)
                }}
                isActionInProgress={isActionInProgress}
                formatTimeAgo={formatTimeAgo}
                        />
            ))}
          </div>
        ) : (
          <div className="space-y-3 pt-6">
            {filteredProjects.map((project: Game, index) => (
              <ArchivedGameCard
                key={`archived-${project.id}`}
                project={project}
                index={index}
                onUnarchive={(project) => {
                  setSelectedProjectForAction(project)
                  setIsArchiveConfirmOpen(true)
                      }}
                formatTimeAgo={formatTimeAgo}
              />
            ))}
          </div>
        );
        })()}
      </div>

      {/* Create Game Modal */}
      <CreateGameModal
        isOpen={showCreateGameModal}
        onClose={() => setShowCreateGameModal(false)}
      />

      {/* Edit Project Modal */}
      {selectedProjectForAction && (
        <ProjectEditModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false)
            setSelectedProjectForAction(null)
          }}
          project={selectedProjectForAction}
          showGenerateButton={selectedProjectForAction.is_editable_in_studio ?? false} // Show for studio projects
          onSave={async (updates) => {
            console.log('💾 Saving project updates:', updates, 'for project:', selectedProjectForAction.id)
            setIsActionInProgress(true)
            
            try {
              await updateProject(selectedProjectForAction.id, {
                content: updates
              })
              
              console.log('✅ Project updated successfully!')
              setIsEditModalOpen(false)
              setSelectedProjectForAction(null)
              
              // Refresh the projects list
              await fetchProjects()
            } catch (error) {
              console.error('❌ Failed to save project updates:', error)
              throw error
            } finally {
              setIsActionInProgress(false)
            }
          }}
        />
      )}

      {/* Archive/Unarchive Confirmation Modal */}
      {selectedProjectForAction && (
        <ConfirmationModal
          isOpen={isArchiveConfirmOpen}
          onClose={() => {
            setIsArchiveConfirmOpen(false)
            setSelectedProjectForAction(null)
          }}
          onConfirm={async () => {
            const isArchived = selectedProjectForAction.archived_at != null
            const action = isArchived ? 'Unarchiving' : 'Archiving'
            const actionPast = isArchived ? 'unarchived' : 'archived'
            
            console.log(`🗄️ ${action} project:`, selectedProjectForAction.name)
            setIsActionInProgress(true)
            
            try {
              if (isArchived) {
                // Unarchive: set status back to 'active'
                await updateProject(selectedProjectForAction.id, {
                  status: 'active'
                })
              } else {
                // Archive: set status to 'deleted'
                await deleteProject(selectedProjectForAction.id)
              }
              
              console.log(`✅ Project ${actionPast} successfully!`)
              
              // Track archive/unarchive
              analyticsService.track({
                name: isArchived ? 'project_unarchived' : 'project_archived',
                properties: {
                  project_id: selectedProjectForAction.id,
                  project_name: selectedProjectForAction.name
                }
              });
              
              setIsArchiveConfirmOpen(false)
              setSelectedProjectForAction(null)
              
              // Refresh the projects list
              await fetchProjects()
          } catch (error) {
              console.error(`❌ Failed to ${action.toLowerCase()} project:`, error)
              showToast('error', `Failed to ${action.toLowerCase()} project. Please try again.`)
            } finally {
              setIsActionInProgress(false)
            }
          }}
          title={selectedProjectForAction.archived_at != null ? 'Unarchive Game' : 'Archive Game'}
          message={
            selectedProjectForAction.archived_at != null
              ? `Are you sure you want to unarchive "${selectedProjectForAction.name}"? It will appear in your active projects.`
              : `Are you sure you want to archive "${selectedProjectForAction.name}"? Archived games can be viewed in the Archived tab.`
          }
          confirmText={selectedProjectForAction.archived_at != null ? 'Unarchive' : 'Archive'}
          cancelText="Cancel"
          isDestructive={selectedProjectForAction.archived_at == null}
          isLoading={isActionInProgress}
      />
      )}

      {/* Transfer Project Modal */}
      {selectedProjectForAction && (
        <ProjectTransferModal
          isOpen={isTransferModalOpen}
          onClose={() => {
            setIsTransferModalOpen(false)
            setSelectedProjectForAction(null)
          }}
          projectId={selectedProjectForAction.id}
          projectName={selectedProjectForAction.name}
          projectIconUrl={selectedProjectForAction.content?.icon_url}
          currentTeamId={selectedProjectForAction.team_id}
          currentTeam={selectedProjectForAction.team}
          onTransfer={handleProjectTransfer}
        />
      )}
    </div>
  )
}

export default ProjectsPage 